<template>
	<el-row>
		<el-col>
			<div class="base-form header">				
				<h2>Пользователи</h2>		
				<el-button type="primary" :icon="circleIcon" size="large" @click="onAddUserClick">
					Новый пользователь
				</el-button>
			</div>
		</el-col>
	</el-row>
	<el-row>
		<el-col :span="4">
			<div class="base-form">
				<el-select 
					v-model="tableRolesFilter"
					multiple
					placeholder="Фильтр по ролям"
					@change="fetchUsersAsync">
					<el-option v-for="e in userRoles"
						:key="e.id"
						:value="e.id"
						:label="e.name">
					</el-option>
				</el-select>
			</div>
		</el-col>
		<el-col :span="12">
			&nbsp;
		</el-col>
		<el-col :span="8" style="text-align: right;">
			<div class="base-form">
				<el-input v-model="tableSearchQuery" style="max-width: 600px" placeholder="Поиск по логину, имени, фамилии, отчеству или телефону"
					class="input-with-select">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</div>
		</el-col>
	</el-row>
	<el-row>
		<el-col :span="24">
			<el-table ref="tableRef"
				row-key="id" 				
				v-loading="tableLoading"
				sortable="custom"
				:data="tableData"				
				:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"				
				@sort-change="onTableSortingChange">
				<el-table-column type="selection" />
				<el-table-column width="100" label="№" prop="id" sortable column-key="id">
					<template #default="scope">		
						<el-link @click="() => onTableLoginCellClick(scope.row)" type="primary" size="small">{{ scope.row.id }}</el-link>
					</template>
				</el-table-column>
				<el-table-column width="200" label="Дата создания" prop="date" sortable column-key="id">
					<template #default="scope">		
						{{ df.dateTime(scope.row.date, true, true) }}
					</template>
				</el-table-column>				
				<el-table-column label="Логин" prop="login" sortable column-key="login">
					<template #default="scope">						
						<el-link @click="() => onTableLoginCellClick(scope.row)" type="primary">
							{{ scope.row.login }}
						</el-link>
						<div>
							<el-tag v-if="scope.row.blocked" type="info" size="small">заблокирован</el-tag>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="Роли">
					<template #default="scope">						
						{{ formatRoles(scope.row.roles) }}
					</template>
				</el-table-column>
				<el-table-column label="Сотрудник" prop="employee.lastname" sortable column-key="lastname">
					<template #default="scope">		
						<el-link type="primary" @click="() => onTableLoginCellClick(scope.row)">
							{{ scope.row.employee?.lastname }} {{ scope.row.employee?.firstname?.substring(0,1) }}.
						</el-link>				
					</template>
				</el-table-column>
				<el-table-column label="Должность, грейд" prop="vin" >
					<template #default="scope">						
						{{ scope.row.employee?.position?.name  }}
						<div>
							<el-text type="info" size="small">{{ scope.row.employee?.positionGrade?.name }}</el-text>
						</div>
					</template>
				</el-table-column>
				<el-table-column align="center">
					<template #default="scope">
						<el-button
							size="small"
							type="danger"
							@click="onDeleteUserClick(scope.row.id)">
							Удалить
						</el-button>
					</template>
				</el-table-column>
			</el-table>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { CirclePlus, Search } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElMessageBox, ElNotification } from 'element-plus';
import axiosHelper from '@/helpers/axiosHelper';
import ApplicationTab from '@/models/ApplicationTab';
import { date as dateFormatter } from '@/helpers/formatters';
import TableFilterModel from '@/models/TableFilterModel';
import UserModel from '@/models/UserModel';
import TabsDictionary from '@/variables/tabs';
import store from '@/store';
import eventHelper from '@/helpers/eventsHelper';
import { WatchStopHandle, watch } from 'vue';
import UserRoleModel from '@/models/UserRoleModel';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElTag, ElLink
	}
})
export default class UsersView extends Vue {
	/** constants */	
	circleIcon = CirclePlus;
	searchIcon = Search;
	userRoles = store.getters.userRoles;
	df = dateFormatter;

	/** view-models */
	tableLoading = false;
	tableData: Array<UserModel> = [];
	tableRolesFilter: Array<number> = [];
	tableSearchQuery = '';
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	tableSortColumn = 'login';
	tableSortOrder = 'ascending';
	
	watchTableSearchQuery: WatchStopHandle | null = null;

	mounted() {
		this.fetchUsersAsync();

		this.watchTableSearchQuery = watch(
			() => this.tableSearchQuery,
			() => this.fetchUsersAsync()
		);

		eventHelper.setEventListener('usersUpdate', () => this.fetchUsersAsync());
	}

	unmounted() {
		if (this.watchTableSearchQuery) {
			this.watchTableSearchQuery();
		}

		eventHelper.removeEventListener('usersUpdate', () => this.fetchUsersAsync());
	}

	/** on tables' cell Login clicked */
	onTableLoginCellClick(row: UserModel) {
		const addUserTab = TabsDictionary.userView;
		addUserTab.params = [ { user: row } ];
		store.commit('addTab', addUserTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onAddUserClick() {
		const addUserTab = TabsDictionary.userAdd;
		const isAddingNewUserForm = true;
		addUserTab.params = [ null, isAddingNewUserForm ];
		store.commit('addTab', addUserTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	async fetchUsersAsync() {		
		try {
			this.tableLoading = true;			
			const filterObject: TableFilterModel = {
				orderBy: this.tableSortColumn ? this.tableSortColumn : 'id',
				orderAsc: this.tableSortOrder == 'ascending' ? true : false,
				query: this.tableSearchQuery,
				roles: this.tableRolesFilter?.join(',') ?? null
			};
			const jwt = store.getters.jwt;
			const usersHttpClient = axiosHelper.createInstance(jwt);
			const response = await usersHttpClient.get('/api/users', { params: filterObject });
			this.tableData = response.data.map((_: any) => new UserModel(_));			
		} catch (e: any) {
			if (e.code == 'ERR_NETWORK') {
				ElNotification({
					title: 'Ошибка',
					message: `Ошибка соединения, не удалось загрузить список пользователей`,
					type: 'error',
				});
			} else {
				ElNotification({
					title: 'Ошибка',
					message: `Ошибка загрузка списка пользователей`,
					type: 'error',
				});
			}
			console.error('Error loading users list', e)
		} finally {
			this.tableLoading = false;
		}
	}

	async onDeleteUserClick(userId: number) {
		ElMessageBox.confirm(
			'Пользователь будет удален',
			{
				confirmButtonText: 'OK',
				cancelButtonText: 'Отмена',
				type: 'error',
			}
		)
		.then(async () => {
			const a = axiosHelper.createInstance();
			try {
				this.tableLoading = true;
				await a.delete(`/api/users/${userId}`);
				ElNotification({
					title: 'Пользователь удален',
					type: 'success',
				});	
				await this.fetchUsersAsync();
				this.tableLoading = false;
			} catch (e) {			
				ElNotification({
					title: 'Ошибка, пользователь не был удален',
					type: 'error',
				});	
				console.error(`Error deleting user #${userId}`, e)
			} finally {
				this.tableLoading = false;
			}
		});		
	}

	formatRoles(roles: any[]) {
		if (!roles?.length) {
			return '';
		}

		let mappedRoles = new Array<string>();

		roles.forEach(r => {
			const mappedRole: UserRoleModel = this.userRoles.find((a: UserRoleModel) => a.id == r?.role?.id);
			if (mappedRole) {
				mappedRoles.push(mappedRole.name);
			}
		});

		return mappedRoles.join(', ');
	}

	onTableSortingChange(a: any) {
		this.tableSortColumn = a.prop ?? null;
		this.tableSortOrder ==  a.order;
		this.fetchUsersAsync();
	}
}
</script>